import './assets/style.css';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.css';
import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createVuetify } from 'vuetify';
import 'vuetify/styles';
import { aliases, fa } from 'vuetify/iconsets/fa';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { Amplify } from 'aws-amplify';
import { JazelAppConfig } from '@jazel/jazel-vuetify';
import App from './App.vue';
import { createAppRouter } from './router';
import '@mdi/font/css/materialdesignicons.css';

Amplify.configure({});

// ---------------------------------------------------------------------------
// Shadow DOM helpers
// ---------------------------------------------------------------------------

/**
 * Create an isolated shadow root for one widget instance.
 * Embed mode mounts into #jazel-door-app wherever it lives in the DOM
 * (parent does not need to be document.body). Local/dev falls back to #app.
 */
function createShadowMount(): {
  shadowRoot: ShadowRoot;
  mountEl: HTMLElement;
  teleportEl: HTMLElement;
} {
  const MOUNT_ID = 'jazel-door-app';
  const isEmbedded = !!window.__JZL_DOOR_EMBEDDED__;

  // Embed: only #jazel-door-app (any parent). Standalone: #app or #jazel-door-app.
  let host = document.getElementById(MOUNT_ID);
  if (!host && !isEmbedded) {
    host = document.getElementById('app');
  }

  if (!host) {
    console.warn(
      `[jzl-door] #${MOUNT_ID} not found — place <div id="${MOUNT_ID}"></div> where the widget should render.`,
    );
    host = document.createElement('div');
    host.id = MOUNT_ID;
    (document.currentScript?.parentElement || document.body).appendChild(host);
  }

  // Drop the loader's leftover #app so it doesn't sit elsewhere on the page
  if (host.id === MOUNT_ID) {
    document.querySelectorAll('#app').forEach((el) => {
      if (el !== host && !el.shadowRoot) el.remove();
    });
  }

  host.classList.add('jzl-door-host');
  host.style.cssText = 'display:block;width:100%;';

  // Reuse existing shadow root if present (e.g. HMR / re-init)
  const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  shadowRoot.innerHTML = '';

  // Base sizing rule — injected before anything else so it wins
  const baseStyle = document.createElement('style');
  baseStyle.textContent =
    ':host{display:block;width:100%;max-height:none}' +
    '#app,.v-application,.v-application__wrap,.v-main{' +
    'width:100%;max-width:100%;max-height:none!important;min-height:0!important;box-sizing:border-box}' +
    '.v-main{padding-top:0!important;padding-bottom:0!important}';
  shadowRoot.appendChild(baseStyle);

  // Widget CSS — scoped to this shadow root (host-page CSS cannot bleed in)
  const cdnUrl = import.meta.env.VITE_CDN_URL;
  if (cdnUrl) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${cdnUrl}/vue-door/cx/css/style.css`;
    shadowRoot.appendChild(link);
  }

  // Vue mount point (inside shadow root)
  const mountEl = document.createElement('div');
  mountEl.id = 'app';
  mountEl.style.width = '100%';
  shadowRoot.appendChild(mountEl);

  // Teleport target — Vuetify dialogs/menus land here (inside shadow root)
  const teleportEl = document.createElement('div');
  teleportEl.setAttribute('data-jzl-teleport', '');
  shadowRoot.appendChild(teleportEl);

  return { shadowRoot, mountEl, teleportEl };
}

/**
 * Watch document.head for any <style> tags injected by Vuetify at runtime
 * (theme vars, component overrides) and move them into the shadow root so
 * they apply inside the widget and don't pollute the host page.
 */
function adoptRuntimeStyles(shadowRoot: ShadowRoot, snapshot: Set<Element>) {
  // Move any styles already injected synchronously during mount()
  document.head.querySelectorAll('style').forEach((el) => {
    if (!snapshot.has(el)) shadowRoot.appendChild(el); // moves, not copies
  });

  // Keep watching for async injections (lazy dialogs, theme updates, etc.)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node instanceof HTMLStyleElement && !snapshot.has(node)) {
          shadowRoot.appendChild(node); // moves node out of <head>
        }
      });
    }
  });
  observer.observe(document.head, { childList: true });
}

// ---------------------------------------------------------------------------
// Core mount function  — one call = one isolated widget instance
// ---------------------------------------------------------------------------
function mountWidget(providedShadowRoot?: ShadowRoot | null) {
  const isEmbedded = !!window.__JZL_DOOR_EMBEDDED__;

  // ── Resolve mount targets — shadow DOM is always used ───────────────────
  let shadowRoot: ShadowRoot | null = providedShadowRoot ?? null;
  let mountEl: Element | null;
  let teleportEl: Element;

  if (!shadowRoot) {
    const created = createShadowMount();
    shadowRoot = created.shadowRoot;
    mountEl    = created.mountEl;
    teleportEl = created.teleportEl;
  } else {
    // Loader already created the shadow root — just find the children
    mountEl    = shadowRoot.querySelector('#app') as Element;
    teleportEl = (shadowRoot.querySelector('[data-jzl-teleport]') ?? document.body) as Element;
  }

  if (!mountEl) {
    console.warn('[jzl-door] Mount target not found — skipping widget init.');
    return;
  }

  // ── Snapshot existing <style> tags before Vuetify injects its own ────────
  const styleSnapshot = shadowRoot
    ? new Set<Element>(document.head.querySelectorAll('style'))
    : null;

  // ── Per-instance Vuetify ─────────────────────────────────────────────────
  const vuetify = createVuetify({
    icons: { defaultSet: 'fa', aliases, sets: { fa } },
    components,
    directives,
    // Route overlay-based components into shadow root so they get scoped CSS
    defaults: {
      VDialog:       { attach: teleportEl },
      VMenu:         { attach: teleportEl },
      VTooltip:      { attach: teleportEl },
      VOverlay:      { attach: teleportEl },
      VSnackbar:     { attach: teleportEl },
      VSelect:       { attach: teleportEl },
      VAutocomplete: { attach: teleportEl },
      VCombobox:     { attach: teleportEl },
    },
  });

  // ── Per-instance Pinia & Router ──────────────────────────────────────────
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);
  const router = createAppRouter();

  // ── Assemble & mount ─────────────────────────────────────────────────────
  const app = createApp(App);
  app.use(vuetify);
  app.use(pinia);
  app.use(router);
  app.use(JazelAppConfig, {
    productId: import.meta.env.VITE_JAZEL_PRODUCT_ID ?? 'vue-door',
    isEmbedded,
    cdnUrl: import.meta.env.VITE_CDN_URL,
    jamsFrontendUrl: import.meta.env.VITE_JAMS_FRONTEND_URL,
    jamsBackendUrl: import.meta.env.VITE_JAMS_BACKEND_URL,
  });

  app.mount(mountEl);

  // ── Adopt runtime styles into shadow root after first render ─────────────
  if (shadowRoot && styleSnapshot) {
    nextTick(() => adoptRuntimeStyles(shadowRoot!, styleSnapshot));
  }
}

// ---------------------------------------------------------------------------
// Queue draining  — process all shadow roots pushed by loader.js
// ---------------------------------------------------------------------------
function drainQueue() {
  const queue = window.__JZL_DOOR_QUEUE__;
  if (!queue) return;
  while (queue.length > 0) {
    mountWidget(queue.shift()!);
  }
}

// Expose __JZL_DOOR_INIT__ so a cached loader can trigger new mounts
window.__JZL_DOOR_INIT__ = drainQueue;

// Boot: use queued shadow roots if available, otherwise create our own
if (window.__JZL_DOOR_QUEUE__?.length) {
  drainQueue();
} else {
  // No queue = either standalone dev mode or old loader (no shadow root)
  mountWidget(null);
}
